import React, { useMemo, useState } from "react";
import { Button, Form, message, Modal, Radio } from "antd";
import LogisticsCompany from "@/enum/LogisticsCompany";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import {
    getAllValidTrackingUrl,
    getValidTrackingUrlByType,
    TrackingWaybillType,
    TrackingWaybillTypeNameMapping,
    ValidTrackingUrlResult,
} from "@/services/trackingUrl";
import { useModalStore } from "@/hooks/Modal/hooks";
import { getRequiredRule } from "@/utils/formValidatorRules";
import { observer } from "mobx-react-lite";

const CACHE_KEY = "TRACKING_URL_SETTING_";

interface TrackingButtonProps {
    logisticsCompany: LogisticsCompany;

    waybillNo?: string;

    onClickAfter?(): void;

    children?: React.ReactNode;
}

interface TrackingSettingModalProps extends InjectBaseModalProps {
    logisticsCompany: LogisticsCompany;

    results: ValidTrackingUrlResult[];

    onChange?(type: string): void;
}

const TrackingSettingModal: React.FC<TrackingSettingModalProps> = React.memo(function (props) {
    const { logisticsCompany, results, open, onCancel, onChange } = props;

    const type = localStorage.getItem(`${CACHE_KEY}_${logisticsCompany}`);
    const [form] = Form.useForm();

    return (
        <Modal
            title="跟踪物流设置"
            width={660}
            open={open}
            onCancel={onCancel}
            okText="保存"
            onOk={() => {
                const { targetType } = form.getFieldsValue();

                localStorage.setItem(`${CACHE_KEY}_${logisticsCompany}`, targetType);

                onChange?.(targetType);

                message.success("保存成功");

                onCancel();
            }}
        >
            <p className="text-error mb5">
                注意：快递官网或者快递100等网站，可能会因为同一台电脑或者IP长期频繁查单号而进行限制。
            </p>
            <p className="text-error font-bold mb20">建议选择跳转随机网站或者定期更换跟踪网站</p>

            <Form form={form}>
                <Form.Item label="当前快递">{LogisticsCompanyNameMap[logisticsCompany]}</Form.Item>

                <Form.Item
                    label="当前跟踪物流网站"
                    name="targetType"
                    initialValue={type || results?.[0]?.type}
                    rules={[getRequiredRule()]}
                >
                    <Radio.Group>
                        <Radio value={TrackingWaybillType.RANDOM} className="mt5 mb10">
                            <span>随机网站</span>

                            <span className="text-error text-small">
                                <span>(推荐，即随机选择</span>
                                {results
                                    .map((result) => {
                                        return TrackingWaybillTypeNameMapping[result.type];
                                    })
                                    .join("，")}
                                <span>查询)</span>
                            </span>
                        </Radio>

                        <div>
                            {results.map((result) => {
                                return (
                                    <Radio value={result.type} key={result.type}>
                                        <span>{TrackingWaybillTypeNameMapping[result.type]}</span>
                                    </Radio>
                                );
                            })}
                        </div>
                    </Radio.Group>
                </Form.Item>
            </Form>
        </Modal>
    );
});

const TrackingButton: React.FC<TrackingButtonProps> = function (props) {
    const { logisticsCompany, waybillNo, onClickAfter } = props;

    const modalStore = useModalStore();

    const [cacheType, setCacheType] = useState(localStorage.getItem(`${CACHE_KEY}_${logisticsCompany}`));
    const results = useMemo(() => getAllValidTrackingUrl(logisticsCompany, waybillNo), [logisticsCompany, waybillNo]);

    const result = useMemo(
        () => getValidTrackingUrlByType(cacheType as TrackingWaybillType, logisticsCompany, waybillNo),
        [cacheType, logisticsCompany, waybillNo],
    );

    return (
        <>
            {results.length ? (
                <Button
                    type="link"
                    onClick={() => {
                        modalStore.openModal(TrackingSettingModal, {
                            logisticsCompany,
                            results,
                            onChange(type) {
                                setCacheType(type);
                            },
                        });
                    }}
                >
                    更换物流跟踪网站
                </Button>
            ) : null}

            {result ? (
                <Button
                    type="primary"
                    className="ant-btn-success"
                    href={result.url}
                    rel="noreferrer"
                    target="_blank"
                    onClick={onClickAfter}
                >
                    <span>跟踪物流</span>
                    <span className="ml5 text-small">
                        (
                        {cacheType === TrackingWaybillType.RANDOM ? (
                            "随机网站"
                        ) : (
                            <span>{TrackingWaybillTypeNameMapping[result.type]}</span>
                        )}
                        )
                    </span>
                </Button>
            ) : null}
        </>
    );
};

export default observer(TrackingButton);
